import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Link,
  Spinner,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/core';
import FileUploadError from '../FileUploadError';
import { MAX_UPLOAD_SIZE_MB } from '../../helpers/fileUpload';
import { fundingDocumentFileUrl } from '../../helpers/documentFiles';

const FUNDING_DOCUMENT_STATUS = {
  missing: { label: 'Manquante', color: 'semiDarkGray.600', icon: null },
  incorrect: { label: 'Incorrecte', color: 'coral.700', icon: 'not-allowed' },
  valid: { label: 'Valide', color: 'semiDarkGray.900', icon: 'check-circle' },
  under_review: { label: "En cours d'étude", color: 'semiDarkGray.900', icon: 'search' },
};

const DOCUMENT_BUTTON_FOCUS = {
  boxShadow: 'none',
  outline: '2px solid',
  outlineColor: 'semiDarkGray.900',
  outlineOffset: '2px',
};

const FundingDocumentStatusIcon = ({ state }) => {
  const status = FUNDING_DOCUMENT_STATUS[state] || FUNDING_DOCUMENT_STATUS.missing;

  if (!status.icon) {
    return (
      <Text
        as="span"
        role="img"
        aria-label={status.label}
        title={status.label}
        color={status.color}
      >
        —
      </Text>
    );
  }

  return (
    <Icon name={status.icon} aria-label={status.label} title={status.label} color={status.color} />
  );
};

export const FundingDocumentsList = ({
  folderId,
  documents,
  pending,
  error,
  uploadPendingKey,
  uploadError,
  uploadStatus,
  canUpload,
  canReview,
  onUpload,
  onReview,
  onRemoveFile,
  reviewPendingId,
  reviewError,
}) => {
  const [refusal, setRefusal] = useState(null);

  return (
    <Box role="region" aria-label="Documents administratifs">
      {pending && <Spinner aria-label="Chargement des documents administratifs" />}
      {!pending && error && (
        <Text color="coral.700" mb={3}>
          Documents administratifs indisponibles pour ce dossier.
        </Text>
      )}
      {!pending && !error && documents.length === 0 && (
        <Text color="semiDarkGray.600">Aucun document administratif requis.</Text>
      )}
      {reviewError && (
        <Text role="alert" color="coral.700" mb={3}>
          {reviewError}
        </Text>
      )}
      {documents.length > 0 && (
        <Box>
          {documents.map(document => {
            const key = document.key || document.documentKey;
            const state = document.state || document.status || 'missing';
            const baseStatus = FUNDING_DOCUMENT_STATUS[state] || FUNDING_DOCUMENT_STATUS.missing;
            const status =
              state === 'valid' && document.reviewRequired === false
                ? { ...baseStatus, label: 'Déposée' }
                : baseStatus;
            const busy = Boolean(uploadPendingKey || reviewPendingId);
            const inputId = `funding-document-${key}`;
            const isUploadable = canUpload && document.uploadable !== false;
            const legacyFiles =
              document.fileDownloadLink || document.originalFilename ? [document] : [];
            const files = document.files?.length ? document.files : legacyFiles;

            return (
              <Box
                key={key}
                mb={3}
                p={3}
                border="1px solid"
                borderColor="lightGray3"
                borderLeft="4px solid"
                borderLeftColor={status.color}
                rounded="md"
                boxShadow="sm"
                onDragOver={event => event.preventDefault()}
                onDrop={event => {
                  event.preventDefault();
                  const selectedFiles = Array.from(event.dataTransfer.files || []);
                  if (
                    isUploadable &&
                    !uploadPendingKey &&
                    !reviewPendingId &&
                    selectedFiles.length
                  ) {
                    onUpload(key, selectedFiles);
                  }
                }}
              >
                <Flex alignItems="flex-start" justifyContent="space-between" gridGap={3}>
                  <Box>
                    <Flex alignItems="center" gridGap={2}>
                      <FundingDocumentStatusIcon state={state} />
                      <Text fontWeight="semibold">{document.name || key}</Text>
                      {document.required === false && (
                        <Text as="span" fontSize="xs" color="semiDarkGray.600">
                          Facultatif
                        </Text>
                      )}
                      {document.hint && (
                        <Tooltip
                          hasArrow
                          label={document.hint}
                          aria-label={document.hint}
                          placement="top"
                          bg="semiDarkGray.900"
                          zIndex="tooltip"
                        >
                          <Box as="span" d="inline-flex" tabIndex={0} aria-label={document.hint}>
                            <Icon name="info-outline" size="14px" color="semiDarkGray.600" />
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                    <Text mt={1} fontSize="xs" color={status.color} fontWeight="semibold">
                      {status.label}
                    </Text>
                  </Box>
                </Flex>
                {files.length > 0 && (
                  <Box as="ul" mt={2} pl={5}>
                    {files.map((file, index) => (
                      <Text
                        as="li"
                        key={file.sharepointPath || `${file.originalFilename}-${index}`}
                        fontSize="sm"
                        overflowWrap="anywhere"
                      >
                        {fundingDocumentFileUrl(folderId, document, file) ? (
                          <Link
                            href={fundingDocumentFileUrl(folderId, document, file)}
                            isExternal
                            color="coral.900"
                          >
                            {file.originalFilename || file.sharepointFilename || 'Consulter'}
                          </Link>
                        ) : (
                          file.originalFilename || file.sharepointFilename
                        )}
                        {document.removable && onRemoveFile && file.sharepointPath && (
                          <Button
                            type="button"
                            size="xs"
                            ml={2}
                            variant="ghost"
                            variantColor={null}
                            color="coral.700"
                            _focus={DOCUMENT_BUTTON_FOCUS}
                            isDisabled={busy}
                            aria-label={`Supprimer ${file.originalFilename ||
                              file.sharepointFilename}`}
                            onClick={() => onRemoveFile(document, file)}
                          >
                            Supprimer
                          </Button>
                        )}
                      </Text>
                    ))}
                  </Box>
                )}
                {document.reviewComment && (
                  <Text mt={1} fontSize="sm" color="coral.700">
                    {document.reviewComment}
                  </Text>
                )}
                {document.locked && (
                  <Flex mt={3} alignItems="center" flexWrap="wrap" gridGap={2}>
                    <Text fontSize="sm" color="semiDarkGray.600">
                      Pièce validée : dévalidez-la pour déposer ou supprimer un fichier.
                    </Text>
                    {canReview && (
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        variantColor={null}
                        borderColor="semiDarkGray.900"
                        color="semiDarkGray.900"
                        _focus={DOCUMENT_BUTTON_FOCUS}
                        isDisabled={busy}
                        onClick={() => onReview(document, 'devalidated')}
                      >
                        Dévalider
                      </Button>
                    )}
                  </Flex>
                )}
                {isUploadable && (
                  <Flex
                    mt={3}
                    p={2}
                    border="1px dashed"
                    borderColor={state === 'incorrect' ? 'coral.400' : 'lightGray3'}
                    rounded="md"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gridGap={2}
                  >
                    <Box>
                      <Text fontSize="sm">Déposez un ou plusieurs PDF ou images ici</Text>
                      <Text fontSize="xs" color="semiDarkGray.600">
                        {MAX_UPLOAD_SIZE_MB} Mo maximum par fichier
                      </Text>
                    </Box>
                    <Input
                      id={inputId}
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      multiple
                      isDisabled={Boolean(uploadPendingKey || reviewPendingId)}
                      display="none"
                      onChange={event => {
                        const input = event.target;
                        const selectedFiles = Array.from(input.files || []);
                        if (!uploadPendingKey && !reviewPendingId && selectedFiles.length)
                          onUpload(key, selectedFiles);
                        input.value = '';
                      }}
                    />
                    <Button
                      as="label"
                      variantColor="neutral"
                      htmlFor={inputId}
                      size="sm"
                      bg="coral.900"
                      color="white"
                      _hover={{ bg: 'semiDarkGray.900' }}
                      _active={{ bg: 'darkGray' }}
                      _focus={DOCUMENT_BUTTON_FOCUS}
                      isLoading={uploadPendingKey === key}
                      isDisabled={Boolean(uploadPendingKey || reviewPendingId)}
                      loadingText="Envoi…"
                      cursor="pointer"
                    >
                      Choisir des fichiers
                    </Button>
                  </Flex>
                )}
                {uploadStatus?.key === key && (
                  <Text role="status" mt={2} fontSize="sm">
                    {uploadStatus.message}
                  </Text>
                )}
                {uploadError?.key === key && (
                  <FileUploadError error={uploadError.message} whiteSpace="pre-line" />
                )}
                {state === 'under_review' && canReview && refusal?.key === key && (
                  <Box mt={3}>
                    <Text
                      as="label"
                      htmlFor={`funding-document-refusal-${key}`}
                      display="block"
                      mb={1}
                      fontSize="sm"
                      fontWeight="semibold"
                    >
                      Motif du refus
                    </Text>
                    <Textarea
                      id={`funding-document-refusal-${key}`}
                      size="sm"
                      value={refusal.comment}
                      placeholder="Précisez ce qui doit être corrigé"
                      onChange={event => setRefusal({ key, comment: event.target.value })}
                    />
                    <Flex mt={2} gridGap={2}>
                      <Button
                        type="button"
                        size="xs"
                        bg="coral.700"
                        variantColor="neutral"
                        color="white"
                        _hover={{ bg: 'red' }}
                        _active={{ bg: 'red' }}
                        _focus={DOCUMENT_BUTTON_FOCUS}
                        isDisabled={
                          Boolean(uploadPendingKey || reviewPendingId) || !refusal.comment.trim()
                        }
                        onClick={() => {
                          onReview(document, 'incorrect', refusal.comment.trim());
                          setRefusal(null);
                        }}
                      >
                        Confirmer le refus
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        variant="ghost"
                        variantColor={null}
                        _focus={DOCUMENT_BUTTON_FOCUS}
                        onClick={() => setRefusal(null)}
                      >
                        Annuler
                      </Button>
                    </Flex>
                  </Box>
                )}
                {state === 'under_review' && canReview && refusal?.key !== key && (
                  <Flex mt={3} gridGap={2}>
                    <Button
                      type="button"
                      size="xs"
                      bg="semiDarkGray.900"
                      variantColor="neutral"
                      color="white"
                      _hover={{ bg: 'darkGray' }}
                      _active={{ bg: 'darkGray' }}
                      _focus={DOCUMENT_BUTTON_FOCUS}
                      isDisabled={Boolean(uploadPendingKey || reviewPendingId)}
                      onClick={() => onReview(document, 'valid')}
                    >
                      Valider
                    </Button>
                    <Button
                      type="button"
                      size="xs"
                      bg="coral.700"
                      variantColor="neutral"
                      color="white"
                      _hover={{ bg: 'red' }}
                      _active={{ bg: 'red' }}
                      _focus={DOCUMENT_BUTTON_FOCUS}
                      isDisabled={Boolean(uploadPendingKey || reviewPendingId)}
                      onClick={() => setRefusal({ key, comment: '' })}
                    >
                      Signaler incorrect
                    </Button>
                  </Flex>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FundingDocumentsList;
